import { type InvoiceItem, type PublicStatement, type StatementItem, type StatementOptions, statementOptionsSchema } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { AfterSend, SendToCustomer } from "../components/email-dialog";
import { StatementView } from "../components/statement-view";
import { Alert, Badge, Button, Checkbox, Dialog, Loading, Panel, PanelHeader, TextAreaField, cx, formatCents, formatDate } from "../components/ui";
import { StatementHeader, StatementTitle } from "./PublicStatement";

const OPTIONS: [keyof StatementOptions, string, string][] = [
  ["lineAmounts", "Line amounts", "Each line's amount."],
  ["quantities", "Quantities", "Quantity × rate on lines that have them."],
  ["invoiceTotals", "Each invoice's totals", "Its total, what's been paid, and its balance."],
  ["dates", "Invoice dates", "Each invoice's date and due date."],
  ["hideRepeatedZero", "Leave out repeated $0 lines", "A $0 line already on an earlier invoice isn't shown again."],
  ["cardOverLimit", "Allow card over the card limit", "Card payment for the whole amount (the surcharge still applies)."],
];

/** Can go on a statement: sent (or brought in and linked to QuickBooks), not void. */
const pickable = (i: InvoiceItem) => i.status !== "void" && (i.source === "crm" ? i.status !== "draft" : !!i.quickbooks);

/**
 * Statement: pick some of the job's invoices and what to show, see exactly what the customer will
 * see (and the PDF), then make it and email or text it.
 */
export function StatementDialog({ jobId, invoices, onClose }: { jobId: string; invoices: InvoiceItem[]; onClose: () => void }) {
  const qc = useQueryClient();
  const choices = invoices.filter((i) => i.status !== "void");
  const [picked, setPicked] = useState<Set<string>>(() => new Set(choices.filter((i) => pickable(i) && i.balanceCents > 0).map((i) => i.id)));
  const [options, setOptions] = useState<StatementOptions>(() => statementOptionsSchema.parse({}));
  const [made, setMade] = useState<{ id: string; url: string } | null>(null);
  const [sending, setSending] = useState(false);
  const body = { invoiceIds: [...picked], options };
  // The preview follows every change (a moment after typing stops).
  const [debounced, setDebounced] = useState(body);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(body), 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, options]);
  const preview = useQuery({ queryKey: ["statement-preview", jobId, debounced], queryFn: () => post<PublicStatement>(`/api/jobs/${jobId}/statements/preview`, debounced), enabled: debounced.invoiceIds.length > 0, placeholderData: (x) => x });
  const pdf = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/jobs/${jobId}/statements/preview.pdf`, { method: "POST", credentials: "include", headers: { "content-type": "application/json", "x-nmt-csrf": "1" }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error("Couldn't make the PDF.");
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    },
  });
  const toggle = (id: string) => setPicked((p) => (p.has(id) ? new Set([...p].filter((x) => x !== id)) : new Set([...p, id])));

  if (sending)
    return (
      <SendToCustomer
        kind="statement"
        id={made?.id ?? ""}
        jobId={jobId}
        title="Send the statement"
        note="Makes the statement and its link. The customer sees it as previewed, with balances as they are when they open it, and can pay everything at once."
        send={async () => {
          const s = await post<StatementItem>(`/api/jobs/${jobId}/statements`, body);
          setMade({ id: s.id, url: s.url! });
          await qc.invalidateQueries({ queryKey: ["statements", jobId] });
          return s.url!;
        }}
        onClose={onClose}
      />
    );

  const total = choices.filter((i) => picked.has(i.id)).reduce((n, i) => n + i.balanceCents, 0);
  return (
    <Dialog
      wide="xl"
      title="Statement"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" busy={pdf.isPending} disabled={!picked.size} onClick={() => pdf.mutate()}>
            Preview PDF
          </Button>
          <Button variant="primary" disabled={!picked.size || !!preview.error} onClick={() => setSending(true)}>
            Send… · {formatCents(total)} due
          </Button>
        </>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,320px)_1fr]">
        <div className="flex flex-col gap-4 text-sm">
          <div>
            <div className="mb-1 font-semibold text-ink-soft">Invoices</div>
            <ul className="rounded-[var(--radius-control)] border border-line">
              {choices.map((i) => {
                const ok = pickable(i);
                return (
                  <li key={i.id} className={cx("border-b border-mist px-3 py-2 last:border-0", !ok && "opacity-60")}>
                    <label className="flex items-start gap-2">
                      <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-brand)]" disabled={!ok} checked={picked.has(i.id)} onChange={() => toggle(i.id)} />
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold">Invoice {i.externalNumber || i.number}</span>
                        {i.title && <span className="text-ink-soft"> · {i.title}</span>}
                        <span className="block text-xs text-muted">
                          {i.invoiceDate || i.sentAt ? formatDate(i.invoiceDate ?? i.sentAt!) : ""} · {formatCents(i.totalCents)} · {i.balanceCents ? `${formatCents(i.balanceCents)} open` : "paid"}
                          {!ok && (i.status === "draft" ? " · a draft: send it first" : " · not linked to QuickBooks")}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="flex flex-col gap-2">
            <div className="font-semibold text-ink-soft">Show</div>
            {OPTIONS.map(([k, label, hint]) => (
              <label key={k} className="flex items-start gap-2" title={hint}>
                <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-brand)]" checked={options[k] as boolean} onChange={(e) => setOptions({ ...options, [k]: e.target.checked })} />
                <span>
                  {label}
                  <span className="block text-xs text-muted">{hint}</span>
                </span>
              </label>
            ))}
          </div>
          <TextAreaField label="Note to the customer (optional)" rows={3} value={options.memo} onChange={(e) => setOptions({ ...options, memo: e.target.value })} />
          {pdf.error && <Alert>{errorMessage(pdf.error)}</Alert>}
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-sm font-semibold text-ink-soft">What the customer sees</div>
          {!picked.size ? (
            <Alert tone="info">Pick at least one invoice.</Alert>
          ) : preview.error ? (
            <Alert>{errorMessage(preview.error)}</Alert>
          ) : !preview.data ? (
            <Loading />
          ) : (
            <div className="overflow-hidden rounded-[var(--radius-panel)] border border-line bg-paper">
              <StatementHeader s={preview.data} />
              <div className={cx("flex flex-col gap-4 p-4 transition-opacity", preview.isFetching && "opacity-70")}>
                <StatementTitle s={preview.data} />
                <StatementView s={preview.data} />
                {preview.data.payableCents > 0 && (
                  <div className="rounded-[var(--radius-panel)] bg-white px-5 py-4 text-sm shadow-[var(--shadow-card)]">
                    <div className="text-lg font-semibold">Pay {formatCents(preview.data.payableCents)}</div>
                    <div className="text-muted">Bank account (free) {preview.data.cardAllowed ? "or card (credit cards have the surcharge)" : `only: cards are for up to ${formatCents(preview.data.cardLimitCents)}`}.</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/** The job's statements: sent, viewed, paid; open one again to resend or void it. */
export function JobStatements({ jobId, canEdit }: { jobId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["statements", jobId], queryFn: () => get<StatementItem[]>(`/api/jobs/${jobId}/statements`) });
  const [resend, setResend] = useState<{ id: string; url: string; how: "email" | "text" } | null>(null);
  const link = useMutation({ mutationFn: async (x: { id: string; how: "email" | "text" }) => ({ ...x, url: (await get<{ url: string }>(`/api/statements/${x.id}/link`)).url }), onSuccess: setResend });
  const voidIt = useMutation({ mutationFn: (id: string) => post(`/api/statements/${id}/void`), onSuccess: () => qc.invalidateQueries({ queryKey: ["statements", jobId] }) });
  if (!q.data?.length) return null;
  return (
    <Panel>
      <PanelHeader title="Statements" description="Several invoices on one page, paid all at once." />
      {(link.error || voidIt.error) && (
        <div className="px-5 pt-3">
          <Alert>{errorMessage(link.error ?? voidIt.error)}</Alert>
        </div>
      )}
      <ul className="px-5 py-2 text-sm">
        {q.data.map((s) => (
          <li key={s.id} className={cx("flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-2 last:border-0", s.voided && "opacity-60")}>
            <span className="font-semibold">Invoices {s.invoiceNumbers.join(", ")}</span>
            <span className="text-muted">{formatDate(s.createdAt)}{s.createdBy ? ` · ${s.createdBy}` : ""}</span>
            {s.voided ? <Badge tone="off">Void</Badge> : s.balanceCents === 0 ? <Badge tone="ok">Paid</Badge> : s.viewedAt ? <Badge tone="brand">Viewed</Badge> : s.sentAt ? <Badge tone="brand">Sent</Badge> : <Badge tone="wait">Not sent</Badge>}
            <span className="flex-1" />
            <span>{formatCents(s.balanceCents)} open</span>
            <a href={`/api/statements/${s.id}/pdf`} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
              PDF
            </a>
            {canEdit && !s.voided && s.balanceCents > 0 && (
              <>
                <button className="font-semibold text-brand hover:underline" onClick={() => link.mutate({ id: s.id, how: "email" })}>
                  Email
                </button>
                <button className="font-semibold text-brand hover:underline" onClick={() => link.mutate({ id: s.id, how: "text" })}>
                  Text
                </button>
                <button className="text-muted hover:text-danger" onClick={() => window.confirm("Void this statement? Its link stops working. The invoices aren't changed.") && voidIt.mutate(s.id)}>
                  Void
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {resend && <AfterSend how={resend.how} kind="statement" id={resend.id} link={resend.url} jobId={jobId} onClose={() => (setResend(null), void qc.invalidateQueries({ queryKey: ["statements", jobId] }))} />}
    </Panel>
  );
}
